import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { DomainContext } from '../domain-repository/DomainRepository'

import { mapLocalizations, transformLocalsArrayToObject } from '../pages/select-language/SelectLanguagePage'
import { getLanguages, getLocalizations } from '../services/localizations'

import Loading from '../components/Loading'

import { saveLocalStorageTranslations } from '../i18n'

export function useKeyValues(_defaultLang, onChange) {
  const { i18n } = useTranslation()
  const { setLanguage, setLocalizationAssets, setLocalizations } = useContext(DomainContext)

  const [isLoading, setIsLoading] = useState(true)
  const [defaultLang, setDefaultLang] = useState(_defaultLang)
  const [languages, setLanguages] = useState([])

  const fetched = useRef(false)

  useEffect(() => {
    if (!defaultLang || fetched.current) { return () => {} }
    setIsLoading(true)

    function positiveHandler(locals) {
      setLanguage(defaultLang)
      i18n.setDefaultNamespace('translations')
      saveLocalStorageTranslations(defaultLang, locals)
      i18n.addResourceBundle(defaultLang, 'translations', locals, true, true)
      i18n.changeLanguage(defaultLang).then(r => {
        if (onChange) {
          onChange(defaultLang)
        }
      })
    }

    getLocalizations({ lang: defaultLang, system: 'KIOSK' })
      .then(res => {
        fetched.current = true

        const mapped = mapLocalizations(defaultLang, defaultLang, res)
        setLocalizations(mapped)

        const mediaAssets = transformLocalsArrayToObject(mapped.filter(r => r.type === 'MEDIA'))
        setLocalizationAssets(mediaAssets)

        const texts = mapped.filter(r => r.type === 'TEXT')
        const obj = transformLocalsArrayToObject(texts)
        positiveHandler(obj)
      })
    .finally(() => setIsLoading(false))

  }, [defaultLang, fetched])

  const handleChangeLanguage = useCallback((key) => {
    setDefaultLang(key)
  }, [])

  return { isLoading, handleChangeLanguage }
}

export default function LanguageInputSection({ onChange, handleLogoClick }) {
  const { i18n } = useTranslation()

  const { setLanguage, setLocalizationAssets, setLocalizations } = useContext(DomainContext)

  const [isLoading, setIsLoading] = useState(false)

  const [defaultLang, setDefaultLang] = useState('en')
  const [languages, setLanguages] = useState([])
  const [optionsOpen, setOptionsOpen] = useState(false)

  useEffect(() => {
    if (isLoading) { return null }
    setIsLoading(true)
    getLanguages().then(res => {
      setLanguages(res)

      const lang = res.find(r => r.isDefault)
      setDefaultLang(lang?.id || 'en')
      setIsLoading(false)
    })
  }, [setLanguages, setIsLoading, setDefaultLang])

  const innerChangeLanguage = (key) => {
    setOptionsOpen(false)

    if (isLoading) { return null }
    setIsLoading(true)

    function positiveHandler(locals) {
      setLanguage(key)
      i18n.setDefaultNamespace('translations')
      saveLocalStorageTranslations(key, locals)
      i18n.addResourceBundle(key, 'translations', locals, true, true)
      i18n.changeLanguage(key).then(r => {
        onChange(key)
      })
    }

    getLocalizations({ lang: key, system: 'KIOSK' })
      .then(res => {
        const mapped = mapLocalizations(key, defaultLang, res)
        setLocalizations(mapped)

        const mediaAssets = transformLocalsArrayToObject(mapped.filter(r => r.type === 'MEDIA'))
        setLocalizationAssets(mediaAssets)

        const texts = mapped.filter(r => r.type === 'TEXT')
        const obj = transformLocalsArrayToObject(texts)
        positiveHandler(obj)
      })
  };

  function delay(func, time) {
    setTimeout(func, time)
  }

  function handlePageClick(e) {
    const element = e.target
    setOptionsOpen((s) => element.id === "dropdown-input" ? !s : false)
  }

  const browserLang = (navigator.language || navigator.userLanguage).slice(0, 2)

  return (
    <div onClick={handlePageClick} className="main">
      <Loading active={isLoading} />
      <div className="pt-4 text-center container">
        <div onClick={handleLogoClick}>
          <img src={require(`../assets/images/logo.png`)} alt="Guerin" />
        </div>
        <div className="mt-5 pt-5 select-language">
          <label>{browserLang === 'pt' ? 'Idioma' : 'Language'}</label>
          <div className="select-custom" >
            <div id="dropdown-input" className={`select-selected ${optionsOpen ? 'select-arrow-active' : ''}`}>{browserLang === 'pt' ? 'Escolher idioma' : 'Select language'}</div>
            <div className={`select-items ${optionsOpen ? '' : 'select-hide'}`}>
              {languages.map(lang => (
                <div style={{ '--flag-img': `url(${lang.imageUrl})` }} key={lang.id} onClick={(e) => { innerChangeLanguage(lang.id); e.stopPropagation(); }} value={lang.id}>{lang.label}</div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
